import { useCallback, useEffect, useRef, useState } from "react";

import { analytics } from "../../lib/telemetry/analytics.ts";

import {
  startAudioCapture,
  type AudioCapture,
  type AudioCaptureStarter,
} from "./audio-capture.ts";
import {
  LIVE_SAMPLE_RATE,
  openLiveTranscription,
  type LiveTranscription,
  type LiveTranscriptionOpener,
} from "./live-transcription.ts";
import {
  DEFAULT_LANGUAGE,
  transcribeWithApi,
  TranscriptionError,
  type Transcriber,
} from "./transcription.ts";

/**
 * ===========================================================================
 * LA MAQUINA DE ESTADOS DEL DICTADO
 * ===========================================================================
 *
 * Aqui vive lo que tiene que ver con el microfono: permisos, grabacion y
 * limpieza. Lo demas entra por la puerta:
 *
 * - `transcribe`  el lote: audio entero -> texto  (`transcription.ts`)
 * - `openLive`    el directo: audio a trozos -> texto provisional y definitivo
 *                 (`live-transcription.ts`)
 * - `startAudioCapture`  el medidor y el PCM     (`audio-capture.ts`)
 *
 * Los dos caminos conviven en cada dictado A PROPOSITO. El directo da la vista
 * previa; el `MediaRecorder` sigue grabando el clip entero por detras, que es
 * lo que hace que perder el socket no cueste nada: se transcribe el clip y el
 * usuario ni se entera de que hubo un plan B.
 *
 * DOS REGLAS que explican casi todo lo de abajo:
 *
 * 1. Ningun estado se queda colgado. Toda salida --exito, error, cancelacion,
 *    desmontaje-- suelta el microfono, cierra el canal y deja el hook en
 *    `idle` o en `error`.
 * 2. Nada se escribe en la caja hasta el desenlace. El texto provisional NO es
 *    texto del usuario: vive aparte, en `interim`, y solo al terminar se
 *    entrega por `onText` una sola vez. Por eso cambiar de camino a mitad no
 *    puede duplicar nada: no hay nada que duplicar todavia.
 */
export type VoiceState =
  /** El navegador no puede grabar: el boton se ofrece apagado. */
  | "unsupported"
  | "idle"
  /** Pidiendo permiso del microfono. Puede tardar: el usuario ve el dialogo. */
  | "starting"
  | "recording"
  /** Se solto el boton y se espera el texto, por directo o por lote. */
  | "transcribing"
  | "error";

export interface UseVoiceRecorderResult {
  readonly state: VoiceState;
  /** Que contarle al usuario cuando `state` es `error`. */
  readonly error: string | null;
  /**
   * Aviso que NO interrumpe: el dictado sigue, pero algo ha cambiado. Hoy solo
   * lo usa la vista previa cuando se cae a mitad.
   */
  readonly notice: string | null;
  /**
   * El texto provisional del directo. Cadena vacia cuando no hay.
   *
   * Todavia NO es texto del usuario: se dibuja aparte, en cursiva, y puede
   * cambiar entero en el siguiente mensaje del servidor.
   */
  readonly interim: string;
  /** Segundos grabados, para el cronometro. */
  readonly seconds: number;
  /** Volumen instantaneo de 0 a 1, para el indicador de captura. */
  readonly level: number;
  /** Hay un dictado en marcha: pidiendo permiso, grabando o esperando texto. */
  readonly active: boolean;
  /** Empieza a grabar. No hace nada si ya se esta grabando. */
  start: () => void;
  /** Termina y transcribe. */
  stop: () => void;
  /** Tira lo grabado --o aborta la transcripcion-- y vuelve a `idle`. */
  cancel: () => void;
  /** Cierra el mensaje de error y vuelve a `idle`. */
  dismissError: () => void;
}

export interface VoiceRecorderOptions {
  /** Que hacer con el texto. El hook no lo envia: lo entrega, una sola vez. */
  onText: (text: string) => void;
  language?: string;
  /**
   * Vocabulario de contexto para el transcriptor por lotes: nombres propios,
   * marcas, siglas. Se lee al transcribir, asi que puede cambiar en cada
   * render.
   */
  prompt?: string;
  /** La pieza por lotes. Por defecto, el API. */
  transcribe?: Transcriber;
  /** La pieza en directo. Por defecto, el WebSocket del API. */
  openLive?: LiveTranscriptionOpener;
  /** El medidor y el PCM. Por defecto, la Web Audio API del navegador. */
  startCapture?: AudioCaptureStarter;
  /** Apagar la vista previa en directo y usar solo el lote. */
  live?: boolean;
}

/**
 * Por debajo de esto no hay una frase, hay un clic sin querer. Se descarta
 * antes de gastar una llamada al API.
 */
const MIN_RECORDING_MS = 700;

/** Un webm de medio segundo de silencio pesa menos que esto. */
const MIN_AUDIO_BYTES = 1200;

/**
 * Tope duro: un microfono abierto y olvidado no graba media hora.
 *
 * Cinco segundos por debajo de los 120 del API --los mismos por lote y por
 * socket-- a proposito: parar justo en el limite es pedir que el redondeo tire
 * la grabacion entera.
 */
const MAX_RECORDING_MS = 115 * 1000;

/** 2400 muestras a 24 kHz son 100 ms: ni un socket de ida y vuelta constante
 *  ni un retraso que se note en la cursiva. */
const LIVE_FRAME_SAMPLES = 2400;

export const useVoiceRecorder = ({
  onText,
  language = DEFAULT_LANGUAGE,
  prompt,
  transcribe = transcribeWithApi,
  openLive = openLiveTranscription,
  startCapture = startAudioCapture,
  live = true,
}: VoiceRecorderOptions): UseVoiceRecorderResult => {
  const [state, setState] = useState<VoiceState>(() =>
    isVoiceRecordingSupported() ? "idle" : "unsupported",
  );
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [interim, setInterim] = useState("");
  const [seconds, setSeconds] = useState(0);
  const [level, setLevel] = useState(0);

  /**
   * El estado tambien en una ref: los callbacks del grabador y del socket
   * viven fuera del render y necesitan leer el estado de AHORA, no el de
   * cuando se crearon.
   */
  const stateRef = useRef(state);
  const sessionRef = useRef<Session | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  /**
   * El contador de generacion es lo que hace que cancelar sea de verdad.
   *
   * Cancelar no puede "deshacer" un `getUserMedia` ya en vuelo ni un `onstop`
   * que el navegador va a disparar igual. Lo que si puede es invalidarlos:
   * cada intento se queda con su numero, cancelar sube el contador, y todo
   * callback que llega con un numero viejo suelta lo que tenga y se calla.
   */
  const generationRef = useRef(0);

  // Lo que cambia en cada render, accesible desde los callbacks.
  const latest = useRef({
    onText,
    language,
    prompt,
    transcribe,
    openLive,
    startCapture,
    live,
  });
  useEffect(() => {
    latest.current = {
      onText,
      language,
      prompt,
      transcribe,
      openLive,
      startCapture,
      live,
    };
  }, [onText, language, prompt, transcribe, openLive, startCapture, live]);

  const goTo = useCallback((next: VoiceState) => {
    stateRef.current = next;
    setState(next);
  }, []);

  /** Cuándo se pulsó el micrófono, para medir el dictado en la telemetría. */
  const startedAtRef = useRef<number | null>(null);

  const fail = useCallback(
    (message: string, kind: string) => {
      analytics.voice.failed({ kind });
      startedAtRef.current = null;
      setError(message);
      setNotice(null);
      setInterim("");
      setSeconds(0);
      setLevel(0);
      goTo("error");
    },
    [goTo],
  );

  /** El dictado ha terminado bien: se entrega el texto una sola vez. */
  const deliver = useCallback(
    (text: string, kind: "live" | "batch" | "rescued") => {
      const startedAt = startedAtRef.current;
      startedAtRef.current = null;
      analytics.voice.completed({
        kind,
        ...(startedAt === null ? {} : { durationMs: Date.now() - startedAt }),
      });
      setInterim("");
      setNotice(null);
      setSeconds(0);
      setLevel(0);
      goTo("idle");
      if (text.trim() !== "") latest.current.onText(text);
    },
    [goTo],
  );

  /**
   * El respaldo: transcribir el clip entero que `MediaRecorder` fue grabando
   * en paralelo.
   *
   * Es seguro SIEMPRE porque el directo no ha escrito nada en la caja todavia
   * --regla 2 de arriba--, asi que aqui no se puede duplicar nada. Y es mejor
   * que quedarse con lo provisional: es el mismo audio, transcrito entero y
   * revisado, en vez de una vista previa a medias.
   */
  const runClip = useCallback(
    (session: Session) => {
      if (session.resolved) return;
      session.resolved = true;
      closeSession(session);
      if (session.generation !== generationRef.current) return;

      const audio = session.clip;
      const durationMs = session.stoppedAt - session.startedAt;

      if (
        audio === null ||
        durationMs < MIN_RECORDING_MS ||
        audio.size < MIN_AUDIO_BYTES
      ) {
        // Lo provisional se rescata solo si no hay nada mejor: sin clip util,
        // es esto o perder lo que el usuario ya ha visto escrito.
        const rescued = session.heard.trim();
        if (rescued !== "") {
          deliver(rescued, "rescued");
          return;
        }
        fail(
          "Ha sido demasiado corto. Habla un momento más antes de parar.",
          "too_short",
        );
        return;
      }

      goTo("transcribing");

      const controller = new AbortController();
      abortRef.current = controller;
      const { transcribe: run, prompt: vocabulary } = latest.current;

      run(
        {
          audio,
          language: latest.current.language,
          // El WebM de MediaRecorder no lleva la duracion en la cabecera, asi
          // que el servidor no la puede medir: se la manda quien si la sabe.
          durationMs,
          ...(vocabulary === undefined ? {} : { prompt: vocabulary }),
        },
        { signal: controller.signal },
      )
        .then((result) => {
          if (session.generation !== generationRef.current) return;
          abortRef.current = null;
          deliver(result.text, "batch");
        })
        .catch((cause: unknown) => {
          if (session.generation !== generationRef.current) return;
          abortRef.current = null;
          // Cancelar no es fallar: no se le enseña un error al usuario por
          // haber hecho lo que pidió.
          if (
            cause instanceof TranscriptionError &&
            cause.code === "cancelled"
          ) {
            goTo("idle");
            setInterim("");
            return;
          }
          // Ultimo recurso: si el lote tambien falla pero el directo llego a
          // dictar algo, se entrega eso antes que dejar al usuario sin nada.
          const rescued = session.heard.trim();
          if (rescued !== "") {
            deliver(rescued, "rescued");
            setNotice(
              "No se pudo revisar la transcripción: revisa el texto antes de enviarlo.",
            );
            return;
          }
          fail(
            cause instanceof Error
              ? cause.message
              : "No se pudo transcribir el audio.",
            "batch",
          );
        });
    },
    [deliver, fail, goTo],
  );

  /** Se pide el clip; si todavia no esta listo, se corre en cuanto lo este. */
  const askForClip = useCallback(
    (session: Session) => {
      session.wantsClip = true;
      if (session.clip !== null || session.stoppedAt !== 0) runClip(session);
    },
    [runClip],
  );

  /** Lo llama `onstop` del grabador: el clip ya esta cerrado. */
  const clipReady = useCallback(
    (session: Session) => {
      releaseRecording(session);
      if (session.generation !== generationRef.current) return;

      session.stoppedAt = Date.now();
      session.clip = new Blob(session.chunks, {
        type: session.recorder.mimeType || FALLBACK_MIME_TYPE,
      });

      // Solo resuelve si alguien ha pedido ya el clip. Mientras el directo
      // siga vivo, el desenlace es suyo.
      if (session.wantsClip) runClip(session);
    },
    [runClip],
  );

  /**
   * Cierra la grabacion y pide el texto.
   *
   * En directo se le pide al servidor el `final` y el clip queda de reserva;
   * si el canal no contesta, el respaldo entra solo. Sin directo, se transcribe
   * el clip.
   */
  const stopRecording = useCallback(
    (session: Session) => {
      if (session.generation !== generationRef.current) return;

      session.capture?.stop();
      session.capture = null;
      session.stopping = true;
      goTo("transcribing");

      // Con directo se espera el `final` y el clip queda de reserva. Sin
      // directo, el clip ES el desenlace.
      if (session.live === null) session.wantsClip = true;
      else session.live.stop();

      stopRecorder(session, clipReady);
    },
    [clipReady, goTo],
  );

  /**
   * Engancha el medidor y, si se puede, la vista previa en directo.
   *
   * Va DESPUES de arrancar el grabador, no antes: montar Web Audio y abrir un
   * socket tarda, y esperar a eso seria perder las primeras palabras. Mientras
   * el canal abre, el audio se va guardando en la pieza de streaming y se
   * manda de golpe al abrir.
   */
  const attachLive = useCallback(
    async (session: Session) => {
      const wantsLive = latest.current.live;

      const capture = await latest.current.startCapture(session.stream, {
        onLevel: setLevel,
        ...(wantsLive
          ? {
              pcm: {
                sampleRate: LIVE_SAMPLE_RATE,
                frameSize: LIVE_FRAME_SAMPLES,
                onChunk: (chunk: ArrayBuffer) => {
                  session.live?.push(chunk);
                },
              },
            }
          : {}),
      });

      // Cancelado mientras se montaba el audio.
      if (session.generation !== generationRef.current || session.closed) {
        capture.stop();
        return;
      }
      session.capture = capture;

      // Sin PCM no hay directo, y no se avisa: en un navegador que nunca lo
      // soporta, repetir el aviso en cada dictado es ruido. El usuario
      // simplemente no ve cursiva, y al soltar tiene su texto igual.
      if (!wantsLive || capture.pcmError !== null) return;

      session.live = latest.current.openLive(
        { language: latest.current.language },
        {
          onOpen: () => {
            session.liveOpened = true;
          },

          onInterim: (text) => {
            if (session.generation !== generationRef.current) return;
            session.heard = text;
            setInterim(text);
          },

          onFinal: (text) => {
            if (session.generation !== generationRef.current) return;

            // El canal se cierra solo al entregar el `final`; se le pide
            // igualmente, que cerrar dos veces no hace nada, y asi la limpieza
            // no depende de que la pieza se porte bien.
            const channel = session.live;
            session.live = null;
            channel?.close();

            if (session.resolved) return;

            /**
             * Un `final` VACIO no es un dictado terminado: es el directo
             * diciendo que no sacó nada en claro.
             *
             * Pasa de verdad --un canal que abre bien y no transcribe-- y
             * tratarlo como exito dejaria al usuario sin nada teniendo el clip
             * entero grabado al lado. Se cae al respaldo, que es el mismo
             * audio por otro camino. Si todavia se esta grabando NO se suelta
             * el microfono: basta con quedarse sin directo, y al parar el clip
             * es el desenlace.
             */
            if (text.trim() === "") {
              setInterim("");
              if (session.stopping) askForClip(session);
              return;
            }

            session.resolved = true;
            releaseRecording(session);
            deliver(text, "live");
          },

          onFailure: () => {
            if (session.generation !== generationRef.current) return;
            session.live = null;
            setInterim("");
            // Si ya se habia soltado el boton, el respaldo entra ahora. Si se
            // sigue grabando, solo se avisa: el clip sigue su curso y al
            // soltar habra texto igual.
            //
            // Y solo si la vista previa llego a existir. Un canal que falla
            // ANTES de abrir --un gateway sin WebSocket, por ejemplo-- no ha
            // enseñado nada que se pueda perder: avisar de que "se ha perdido"
            // seria contar un fallo que el usuario no ha visto, en cada
            // dictado. Sin directo, simplemente no hay cursiva.
            if (session.stopping) askForClip(session);
            else if (session.liveOpened) setNotice(DEGRADED_NOTICE);
          },

          onClosed: () => {
            if (session.generation !== generationRef.current) return;
            session.live = null;
            setInterim("");
            if (session.stopping) askForClip(session);
          },
        },
      );
    },
    [askForClip, deliver],
  );

  const begin = useCallback(
    (generation: number, stream: MediaStream) => {
      let recorder: MediaRecorder;
      const mimeType = pickAudioMimeType();
      try {
        recorder = new MediaRecorder(
          stream,
          mimeType === undefined ? {} : { mimeType },
        );
        recorder.start();
      } catch {
        stopTracks(stream);
        fail(
          "Este navegador no ha podido abrir el grabador de audio.",
          "recorder",
        );
        return;
      }

      const session: Session = {
        generation,
        stream,
        recorder,
        chunks: [],
        clip: null,
        startedAt: Date.now(),
        stoppedAt: 0,
        capture: null,
        live: null,
        heard: "",
        liveOpened: false,
        stopping: false,
        wantsClip: false,
        resolved: false,
        timer: null,
        limit: null,
        closed: false,
      };
      sessionRef.current = session;

      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) session.chunks.push(event.data);
      };
      recorder.onerror = () => {
        closeSession(session);
        if (session.generation !== generationRef.current) return;
        sessionRef.current = null;
        fail("Se ha cortado la grabación. Inténtalo otra vez.", "interrupted");
      };
      recorder.onstop = () => {
        clipReady(session);
      };

      session.timer = setInterval(() => {
        setSeconds(Math.floor((Date.now() - session.startedAt) / 1000));
      }, 250);
      session.limit = setTimeout(() => {
        stopRecording(session);
      }, MAX_RECORDING_MS);

      goTo("recording");
      void attachLive(session);
    },
    [attachLive, clipReady, fail, goTo, stopRecording],
  );

  const start = useCallback(() => {
    // Desde `error` se puede reintentar directamente: el mensaje anterior no
    // obliga a pasar por un botón de "entendido".
    if (stateRef.current !== "idle" && stateRef.current !== "error") return;

    const generation = ++generationRef.current;
    startedAtRef.current = Date.now();
    analytics.voice.started({ kind: "dictation" });
    setError(null);
    setNotice(null);
    setInterim("");
    setSeconds(0);
    setLevel(0);
    goTo("starting");

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        // Cancelado mientras el navegador preguntaba: se suelta el micrófono.
        // Sin esto quedaría encendido el punto rojo de la pestaña para
        // siempre, grabando para nadie.
        if (generation !== generationRef.current) {
          stopTracks(stream);
          return;
        }
        begin(generation, stream);
      })
      .catch((cause: unknown) => {
        if (generation !== generationRef.current) return;
        fail(microphoneMessage(cause), "microphone");
      });
  }, [begin, fail, goTo]);

  const stop = useCallback(() => {
    const session = sessionRef.current;
    if (session === null || stateRef.current !== "recording") return;
    stopRecording(session);
  }, [stopRecording]);

  const cancel = useCallback(() => {
    if (stateRef.current === "unsupported") return;
    if (stateRef.current !== "idle" && stateRef.current !== "error") {
      analytics.voice.cancelled({ kind: stateRef.current });
    }
    startedAtRef.current = null;

    generationRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;

    const session = sessionRef.current;
    if (session !== null) {
      // Se para el grabador igualmente, para soltar el micrófono. Su `onstop`
      // verá que la generación ya no es la suya y no transcribirá nada.
      silence(session.recorder);
      closeSession(session);
      sessionRef.current = null;
    }

    setError(null);
    setNotice(null);
    setInterim("");
    setSeconds(0);
    setLevel(0);
    goTo("idle");
  }, [goTo]);

  const dismissError = useCallback(() => {
    if (stateRef.current !== "error") return;
    setError(null);
    goTo("idle");
  }, [goTo]);

  // Al desmontar --cambiar de vista, cerrar la pestaña-- se suelta todo. Un
  // micrófono abierto sin interfaz que lo apague es lo peor que puede pasar.
  useEffect(
    () => () => {
      generationRef.current += 1;
      abortRef.current?.abort();
      const session = sessionRef.current;
      if (session !== null) {
        silence(session.recorder);
        closeSession(session);
        sessionRef.current = null;
      }
    },
    [],
  );

  return {
    state,
    error,
    notice,
    interim,
    seconds,
    level,
    active:
      state === "starting" || state === "recording" || state === "transcribing",
    start,
    stop,
    cancel,
    dismissError,
  };
};

/**
 * Hay navegadores sin MediaRecorder y contextos sin `mediaDevices` --una
 * página servida por http, que no es contexto seguro--. Los tipos del DOM dan
 * las dos cosas por hechas; en tiempo de ejecución no lo están, así que se
 * comprueba contra `globalThis` sin tipos.
 */
export const isVoiceRecordingSupported = (): boolean => {
  const scope = globalThis as {
    MediaRecorder?: unknown;
    navigator?: { mediaDevices?: { getUserMedia?: unknown } };
  };
  return (
    typeof scope.MediaRecorder === "function" &&
    typeof scope.navigator?.mediaDevices?.getUserMedia === "function"
  );
};

/**
 * El clip de respaldo. Cada navegador graba en lo suyo: Chrome y Firefox dan
 * webm/opus, Safari da mp4.
 *
 * Ojo, esto es SOLO el respaldo por lotes. El directo no pasa por
 * `MediaRecorder` en absoluto: manda PCM crudo, que es lo unico que acepta la
 * API realtime del proveedor.
 *
 * Ogg NO esta en la lista aunque algun Firefox lo sepa producir: el API no lo
 * acepta, asi que pedirlo seria grabar algo que se va a rechazar.
 */
const MIME_TYPE_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
];

const FALLBACK_MIME_TYPE = "audio/webm";

const pickAudioMimeType = (): string | undefined => {
  const supported = (
    MediaRecorder as { isTypeSupported?: (type: string) => boolean }
  ).isTypeSupported;
  if (typeof supported !== "function") return undefined;
  return MIME_TYPE_CANDIDATES.find((type) => supported(type));
};

interface Session {
  readonly generation: number;
  readonly stream: MediaStream;
  readonly recorder: MediaRecorder;
  readonly chunks: Blob[];
  /** El clip cerrado, cuando el grabador ya ha parado. */
  clip: Blob | null;
  readonly startedAt: number;
  stoppedAt: number;
  capture: AudioCapture | null;
  live: LiveTranscription | null;
  /**
   * Lo ultimo que dijo el directo. NO se borra cuando el canal se cae.
   *
   * Es distinto del `interim` que se dibuja: aquel se limpia en cuanto el
   * directo deja de valer, porque enseñar una cursiva que ya no se mueve
   * engaña. Esto se guarda para el ultimo recurso -- si el respaldo TAMBIEN
   * falla, es la diferencia entre devolverle algo al usuario o nada.
   */
  heard: string;
  /**
   * El directo llego a abrir. Distingue "se ha perdido la vista previa" de
   * "nunca la hubo", que no merece aviso.
   */
  liveOpened: boolean;
  /**
   * Se solto el boton: este dictado tiene que acabar en un desenlace.
   *
   * Separado de `wantsClip` a proposito. Mientras se espera el `final`, el
   * dictado esta CERRANDO pero el clip todavia no hace falta; si el canal se
   * cae justo entonces, es esto lo que distingue "avisa y sigue grabando" de
   * "resuelve ya con el clip". Con una sola bandera, esa caida no despertaba
   * a nadie y el estado se quedaba en `transcribing` para siempre.
   */
  stopping: boolean;
  /** El desenlace depende ya del clip: en cuanto este listo, se transcribe. */
  wantsClip: boolean;
  /** Este dictado ya tiene desenlace: no puede tener otro. */
  resolved: boolean;
  timer: ReturnType<typeof setInterval> | null;
  limit: ReturnType<typeof setTimeout> | null;
  closed: boolean;
}

/**
 * Suelta el microfono, el medidor y los cronometros. NO toca el canal.
 *
 * Idempotente a proposito: `cancel` y `onstop` pueden llegar los dos.
 */
const releaseRecording = (session: Session): void => {
  if (session.closed) return;
  session.closed = true;

  if (session.timer !== null) clearInterval(session.timer);
  if (session.limit !== null) clearTimeout(session.limit);
  session.capture?.stop();
  session.capture = null;
  stopTracks(session.stream);
};

/**
 * Ademas del microfono, cierra el canal en directo.
 *
 * La diferencia con `releaseRecording` es la que hacia que el dictado se
 * colgara: en directo el grabador para PRIMERO y el `final` llega DESPUES, asi
 * que cerrar el canal al parar el grabador es colgar el telefono justo antes
 * de que contesten. Esto es solo para cuando el dictado TERMINA de verdad --
 * cancelar, desmontar, resolver--, nunca para cuando el grabador se para.
 */
const closeSession = (session: Session): void => {
  releaseRecording(session);
  session.live?.close();
  session.live = null;
};

const stopTracks = (stream: MediaStream): void => {
  for (const track of stream.getTracks()) track.stop();
};

/**
 * Para el grabador. Si ya estaba parado no habrá `onstop`, así que se cierra
 * la grabación a mano: si no, el estado se quedaría esperando para siempre.
 */
const stopRecorder = (
  session: Session,
  ready: (session: Session) => void,
): void => {
  if (session.recorder.state === "inactive") {
    ready(session);
    return;
  }
  try {
    session.recorder.stop();
  } catch {
    ready(session);
  }
};

/** Parar el grabador sin que importe el resultado: solo suelta el micrófono. */
const silence = (recorder: MediaRecorder): void => {
  try {
    if (recorder.state !== "inactive") recorder.stop();
  } catch {
    // Ya estaba parado o el navegador se quejó: da igual, las pistas se
    // cierran de todos modos en `closeSession`.
  }
};

/**
 * Perder el directo a mitad no es un error: el clip sigue grabando y al soltar
 * habrá texto igual. Lo que cambia es que la cursiva deja de moverse, y eso sí
 * hay que decirlo o parece que se ha colgado.
 *
 * El motivo del servidor NO se enseña aquí, aunque venga escrito para el
 * usuario. Un fallo del canal puede ser algo sobre lo que él no puede hacer
 * nada --"llegó audio antes del mensaje start" es un fallo de protocolo entre
 * dos programas-- y ponérselo delante mientras habla es ruido que además
 * asusta. Cuando el motivo SÍ importa --cuando no hay texto que darle-- se
 * enseña entero, en el estado de error.
 */
const DEGRADED_NOTICE =
  "Se ha perdido la vista previa, pero se sigue grabando: al terminar se transcribirá igual.";

/**
 * El motivo por el que no hay micrófono cambia lo que el usuario tiene que
 * hacer, así que cada uno tiene su mensaje. Un "no se pudo grabar" genérico
 * deja a quien lo bloqueó sin saber que puede desbloquearlo.
 */
const microphoneMessage = (cause: unknown): string => {
  const name = cause instanceof Error ? cause.name : "";

  switch (name) {
    case "NotAllowedError":
    case "PermissionDeniedError":
    case "SecurityError":
      return "No has dado permiso para usar el micrófono. Actívalo en los permisos del navegador y vuelve a intentarlo.";
    case "NotFoundError":
    case "DevicesNotFoundError":
      return "No se ha encontrado ningún micrófono conectado.";
    case "NotReadableError":
    case "TrackStartError":
      return "El micrófono lo está usando otra aplicación.";
    default:
      return "No se pudo abrir el micrófono. Inténtalo otra vez.";
  }
};
